// Animationer för "Monter - live" när den publiceras eller avpubliceras under
// besöket. Animeringen görs direkt på sektionernas element (utan omslag), så
// att DOM-strukturen och sidans :first-child-regler inte ändras.
//
// För att det ska bli mjukt animeras aldrig höjd eller padding, eftersom det
// tvingar webbläsaren att räkna om layouten för hela sidan i varje bildruta.
// I stället ändras layouten en gång och sektionerna glider på plats med
// transform (FLIP), medan montern tonas in eller ut med opacity.

const DURATION = 500;
const FADE_DURATION = 250;
const EASING = "cubic-bezier(0.2, 0, 0, 1)";

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const findMonterElement = (sectionId: string) =>
  document.querySelector<HTMLElement>(
    `[data-monter-section="${CSS.escape(sectionId)}"]`,
  );

const getSections = () =>
  Array.from(document.querySelectorAll<HTMLElement>("[data-page-section]"));

// Innehållets överkant, så att även ändrad padding räknas med
const contentTop = (el: HTMLElement) =>
  el.getBoundingClientRect().top + parseFloat(getComputedStyle(el).paddingTop);

type Snapshot = Map<HTMLElement, number>;

// Sparar var sektionerna ligger innan layouten ändras
export const snapshotSections = (): Snapshot =>
  new Map(getSections().map((el) => [el, contentTop(el)]));

// Låter sektionerna glida från sitt gamla läge till det nya
export const playSectionShift = (snapshot: Snapshot) => {
  if (prefersReducedMotion()) return [];

  const animations: Animation[] = [];
  snapshot.forEach((before, el) => {
    if (!el.isConnected) return;

    const delta = before - contentTop(el);
    if (Math.abs(delta) < 1) return;

    animations.push(
      el.animate(
        [{ transform: `translateY(${delta}px)` }, { transform: "none" }],
        { duration: DURATION, easing: EASING },
      ),
    );
  });
  return animations;
};

export const animateMonterIn = (el: HTMLElement) => {
  if (prefersReducedMotion()) return;

  // Fördröjd så att innehållet under hinner glida undan först
  el.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: DURATION,
    delay: DURATION / 3,
    easing: EASING,
    fill: "backwards",
  });
};

// Stilar som ligger kvar tills sidan uppdaterats och React tagit bort montern
const heldCleanups: (() => void)[] = [];

export const releaseHeldAnimations = () => {
  heldCleanups.splice(0).forEach((cleanup) => cleanup());
};

const holdStyles = (el: HTMLElement, styles: Partial<CSSStyleDeclaration>) => {
  const previous = el.getAttribute("style");
  Object.assign(el.style, styles);
  heldCleanups.push(() => {
    if (previous === null) el.removeAttribute("style");
    else el.setAttribute("style", previous);
  });
};

// Tonar ut montern, fäller sedan ihop den på en gång och låter innehållet
// under glida upp. Montern ligger kvar ihopfälld tills sidan uppdateras.
export const animateMonterOut = async (el: HTMLElement) => {
  if (prefersReducedMotion()) return;

  const fade = el.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: FADE_DURATION,
    easing: "ease-out",
    fill: "forwards",
  });
  heldCleanups.push(() => fade.cancel());
  await fade.finished.catch(() => {});

  const snapshot = snapshotSections();
  snapshot.delete(el);

  // Sektionen under får direkt den padding den har när montern är borta,
  // t.ex. ingen padding överst när den blir första sektion
  const next = el.nextElementSibling;
  if (next instanceof HTMLElement && next.hasAttribute("data-page-section")) {
    const padding = paddingInPlaceOf(next, el);
    holdStyles(next, {
      paddingTop: padding.top,
      paddingBottom: padding.bottom,
    });
  }

  holdStyles(el, {
    height: "0px",
    paddingTop: "0px",
    paddingBottom: "0px",
    overflow: "hidden",
  });

  await Promise.all(
    playSectionShift(snapshot).map((a) => a.finished.catch(() => {})),
  );
};

type Padding = { top: string; bottom: string };

// Vilken padding `el` får när den hamnar på `target`s plats, t.ex. när den
// blir första sektion. Räknas ut med en tom, dold kopia på den platsen.
const paddingInPlaceOf = (el: HTMLElement, target: HTMLElement): Padding => {
  const probe = el.cloneNode(false) as HTMLElement;
  probe.style.display = "none";
  target.before(probe);
  const style = getComputedStyle(probe);
  const padding = { top: style.paddingTop, bottom: style.paddingBottom };
  probe.remove();
  return padding;
};
